import harvestUrl from '../assets/audio/crystal-reserve.mp3';
import nightUrl from '../assets/audio/nappes-nocturnes.mp3';

export type AmbientBed = 'night' | 'harvest';

const FADE_OUT_SECONDS = 1;
const FADE_IN_SECONDS = 1;
const LEVEL = 0.22;
const SILENT = 0.0008;

let desired: AmbientBed = 'night';
let soundOn = true;
let context: AudioContext | null = null;
let nightGain: GainNode | null = null;
let harvestGain: GainNode | null = null;
let nightAudio: HTMLAudioElement | null = null;
let harvestAudio: HTMLAudioElement | null = null;
let active: AmbientBed | null = null;
let generation = 0;

function gainOf(bed: AmbientBed) {
  return bed === 'night' ? nightGain : harvestGain;
}

function audioOf(bed: AmbientBed) {
  return bed === 'night' ? nightAudio : harvestAudio;
}

function held(param: AudioParam): number {
  const now = context?.currentTime ?? 0;
  if (typeof param.cancelAndHoldAtTime === 'function') param.cancelAndHoldAtTime(now);
  return Math.max(0, param.value);
}


function glide(gain: GainNode, to: number, seconds: number) {
  if (!context) return;
  const now = context.currentTime;
  const param = gain.gain;
  const from = Math.max(held(param), to === 0 ? SILENT : SILENT);
  param.setValueAtTime(from, now);
  if (seconds <= 0.05 || Math.abs(from - Math.max(to, SILENT)) < 0.0002) {
    param.setValueAtTime(to, now);
    return;
  }
  param.exponentialRampToValueAtTime(Math.max(to, SILENT), now + seconds);
  if (to === 0) param.setValueAtTime(0, now + seconds);
}

function wait(ms: number, token: number) {
  return new Promise<boolean>((resolve) => {
    window.setTimeout(() => resolve(token === generation), ms);
  });
}

function fadeSeconds(current: number, full: number) {
  const ratio = Math.min(1, Math.max(0, current / LEVEL));
  return Math.max(0.25, full * (full === FADE_OUT_SECONDS ? ratio : 1 - ratio));
}

function pageIsAudible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}


function hush() {
  generation += 1;
  nightAudio?.pause();
  harvestAudio?.pause();
  const now = context?.currentTime ?? 0;
  for (const gain of [nightGain, harvestGain]) {
    if (!gain) continue;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(0, now);
  }
  active = null;
  const ctx = context;
  const silenced = generation;
  if (ctx?.state === 'running') {
    void ctx.suspend().then(() => {
      if (generation !== silenced && pageIsAudible() && soundOn && ctx.state === 'suspended') void ctx.resume();
    });
  }
}

async function awaken() {
  if (!soundOn || !context || !pageIsAudible()) return;
  try {
    if (context.state === 'suspended') await context.resume();
  } catch {
    return;
  }
  if (!pageIsAudible()) {
    hush();
    return;
  }
  void settle();
}

function watchPageVisibility() {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hush();
    else void awaken();
  });
  window.addEventListener('pagehide', () => hush());
  window.addEventListener('pageshow', () => {
    if (document.visibilityState === 'visible') void awaken();
  });
}

watchPageVisibility();

async function settle() {
  const token = ++generation;
  if (!pageIsAudible() || !context || !nightGain || !harvestGain) return;

  if (active && (!soundOn || active !== desired)) {
    const bed = active;
    const gain = gainOf(bed);
    const audio = audioOf(bed);
    if (gain && audio) {
      const current = Math.max(held(gain.gain), 0);
      const seconds = fadeSeconds(current, FADE_OUT_SECONDS);
      glide(gain, 0, seconds);
      const still = await wait(seconds * 1000, token);
      if (!still) return;
      gain.gain.setValueAtTime(0, context.currentTime);
      audio.pause();
      if (active === bed) active = null;
    }
  }

  if (token !== generation || !soundOn) return;

  const next = desired;
  const gain = gainOf(next);
  const audio = audioOf(next);
  if (!gain || !audio) return;

  if (active === next) {
    const current = Math.max(held(gain.gain), 0);
    glide(gain, LEVEL, fadeSeconds(current, FADE_IN_SECONDS));
    return;
  }

  gain.gain.cancelScheduledValues(context.currentTime);
  gain.gain.setValueAtTime(0, context.currentTime);
  try {
    await audio.play();
  } catch {
    return;
  }
  if (token !== generation || !soundOn || desired !== next || !pageIsAudible()) {
    audio.pause();
    gain.gain.setValueAtTime(0, context.currentTime);
    return;
  }
  active = next;
  gain.gain.setValueAtTime(SILENT, context.currentTime);
  glide(gain, LEVEL, FADE_IN_SECONDS);
}

function ensureGraph(ctx: AudioContext) {
  if (nightGain && harvestGain && nightAudio && harvestAudio) return;
  context = ctx;
  nightAudio = new Audio(nightUrl);
  harvestAudio = new Audio(harvestUrl);
  for (const audio of [nightAudio, harvestAudio]) {
    audio.loop = true;
    audio.preload = 'auto';
    audio.disableRemotePlayback = true;
  }
  nightGain = ctx.createGain();
  harvestGain = ctx.createGain();
  nightGain.gain.value = 0;
  harvestGain.gain.value = 0;
  ctx.createMediaElementSource(nightAudio).connect(nightGain);
  ctx.createMediaElementSource(harvestAudio).connect(harvestGain);
  nightGain.connect(ctx.destination);
  harvestGain.connect(ctx.destination);
}


export function primeAmbient(ctx: AudioContext) {
  ensureGraph(ctx);
  if (!soundOn) return;
  void ctx.resume();
  void settle();
}

export function setAmbientEnabled(next: boolean) {
  soundOn = next;
  if (next && context) void context.resume();
  void settle();
}

export function setAmbientBed(bed: AmbientBed) {
  if (desired === bed) return;
  desired = bed;
  void settle();
}
